import type { FC, ReactNode } from 'react'
import type { ProjectSummary } from '@/types'

import Image from 'next/image'
import Link from 'next/link'
import { useState } from 'react'
import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { pad } from '@/lib/format'
import { revealDelay } from '@/lib/motion'
import { HeroTitle } from '@/components/generals/hero-title'
import { Mark } from '@/components/generals/mark'
import { PlaceholderTag } from '@/components/generals/placeholder-tag'
import { SectionLabel } from '@/components/generals/section-label'

interface WorkHeroProps {
    projects: ProjectSummary[]
}

/* Work index: the headline and a list of case studies, with a preview frame that follows the row in focus. */
const WorkHero: FC<WorkHeroProps> = ({ projects }): ReactNode => {
    const [active, setActive] = useState(0)

    const current = projects[active] ?? projects[0]
    const hasConcepts = projects.some((project) => project.isConcept)

    return (
        <section id='work-hero' aria-labelledby='work-hero-title' data-nav-theme='dark' className='relative overflow-hidden bg-charcoal text-cream'>
            <div className='mx-auto max-w-[1440px] px-5 pb-16 pt-32 md:px-10 md:pb-24 md:pt-40'>
                <div className='relative flex flex-wrap items-center justify-between gap-x-6 gap-y-3 pb-5'>
                    <span aria-hidden='true' className='reveal-rule absolute inset-x-0 bottom-0 h-px bg-cream/15' style={revealDelay(100)} />
                    <SectionLabel tone='dark' className='reveal-fade'>Our work</SectionLabel>
                    {projects.length > 0 && (
                        <p className='reveal-fade font-mono text-[11px] uppercase tracking-[0.2em] text-cream/65' style={revealDelay(150)}>
                            {pad(projects.length)} case studies
                        </p>
                    )}
                </div>

                <div className='mt-10 grid gap-12 md:mt-14 lg:grid-cols-12 lg:gap-10'>
                    <div className='lg:col-span-7'>
                        <HeroTitle
                            id='work-hero-title'
                            className='type-hero'
                            delay={200}
                            lines={[
                                'Work that moves',
                                <>brands <Mark tone='dark'>forward.</Mark></>
                            ]}
                        />

                        <div className='reveal-fade mt-7 flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between' style={revealDelay(420)}>
                            <p className='max-w-md text-[17px] leading-relaxed text-cream/80'>
                                A look at how we position brands, build campaigns and turn creative ideas into measurable growth.
                            </p>
                            {hasConcepts && <PlaceholderTag tone='dark' className='self-start sm:self-end'>Includes concept projects</PlaceholderTag>}
                        </div>

                        {projects.length > 0 && (
                            <nav aria-label='Case studies' className='mt-12 md:mt-14'>
                                <ol className='border-t border-cream/15'>
                                    {projects.map((project, index) => (
                                        <li key={project.id} className='reveal-fade border-b border-cream/15' style={revealDelay(520 + index * 70)}>
                                            <Link
                                                href={`/work/${project.slug}`}
                                                onMouseEnter={() => setActive(index)}
                                                onFocus={() => setActive(index)}
                                                className='group grid grid-cols-[2.25rem_1fr_auto] items-center gap-4 py-4 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-coral md:grid-cols-[3rem_minmax(0,1fr)_minmax(0,1fr)_auto] md:py-5'
                                            >
                                                <span className='font-mono text-xs text-cream/60'>{pad(index + 1)}</span>
                                                <span
                                                    className={cn(
                                                        'font-heading font-wide text-xl font-black uppercase tracking-[-0.02em] transition-[translate,color] duration-500 ease-[var(--ease-out-expo)] group-hover:translate-x-2 md:text-2xl',
                                                        index === active && 'lg:text-coral'
                                                    )}
                                                >
                                                    {project.name}
                                                </span>
                                                <span className='hidden text-[14px] text-cream/70 md:block'>
                                                    {[project.industry, project.category].filter(Boolean).join(' / ')}
                                                </span>
                                                <ArrowUpRight aria-hidden='true' className='size-5 shrink-0 transition-transform duration-500 ease-[var(--ease-out-expo)] group-hover:rotate-45' />
                                            </Link>
                                        </li>
                                    ))}
                                </ol>
                            </nav>
                        )}
                    </div>

                    {current && (
                        <figure aria-hidden='true' className='relative hidden lg:col-span-4 lg:col-start-9 lg:block lg:self-end'>
                            <div className='reveal-clip absolute -left-5 -top-5 h-1/3 w-1/2 bg-coral' style={revealDelay(300)} />
                            <div className='reveal-clip relative aspect-[4/5] overflow-hidden bg-charcoal-soft' style={revealDelay(380)}>
                                {projects.map((project, index) => project.cover && (
                                    <Image
                                        key={project.id}
                                        src={project.cover}
                                        alt=''
                                        fill
                                        sizes='(min-width: 1024px) 32vw, 1px'
                                        className={cn(
                                            'object-cover transition-[opacity,scale] duration-700 ease-[var(--ease-out-expo)]',
                                            index === active ? 'scale-100 opacity-100' : 'scale-[1.06] opacity-0'
                                        )}
                                    />
                                ))}
                                <span className='absolute bottom-0 left-0 bg-cream px-3 py-2 font-heading font-wide text-xl font-black uppercase leading-none tracking-[-0.03em] text-charcoal'>
                                    {pad(active + 1)}
                                </span>
                            </div>
                            <figcaption className='reveal-fade mt-4 flex justify-between gap-4 font-mono text-[10px] uppercase tracking-[0.2em] text-cream/60' style={revealDelay(800)}>
                                <span>{current.name} / {current.industry}</span>
                                <span>{current.isConcept ? 'Concept' : current.year}</span>
                            </figcaption>
                        </figure>
                    )}
                </div>
            </div>
        </section>
    )
}

export default WorkHero
